import React, { useState, useEffect, useRef } from "react";
import { Icon } from "@iconify/react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import ThemeToggleButton from "../helper/ThemeToggleButton";
import { useAuth } from "../context/AuthContext";

const MasterLayout = ({ children }) => {
  const [sidebarActive, setSidebarActive] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false); // State for dropdown
  const dropdownRef = useRef(null);
  
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Handle clicking outside to close the dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/signin");
  };

  const getInitial = () => {
    return user?.name ? user.name.charAt(0).toUpperCase() : "U";
  };

  return (
    <section className={mobileMenu ? "overlay active" : "overlay"}>
      {/* ========== SIDEBAR: Restored and Fixed ========== */}
      <aside className={`sidebar ${sidebarActive ? "active" : ""} ${mobileMenu ? "sidebar-open" : ""}`}>
        <button onClick={() => setMobileMenu(false)} type="button" className="sidebar-close-btn">
          <Icon icon="radix-icons:cross-2" />
        </button>

        <div className="sidebar-header">
          <Link to="/" className="sidebar-logo">
            <img src="/assets/images/logo.png" className="light-logo" alt="logo" />
            <img src="/assets/images/logo-light.png" className="dark-logo" alt="logo" />
            <img src="/assets/images/logo-icon.png" className="logo-icon" alt="mini logo" />
          </Link>
        </div>

        <div className="sidebar-menu-area">
          <ul className="sidebar-menu">
            <li className="sidebar-menu-group-title">Top Locations</li>
            <li><NavLink to="/locations-top" className={({isActive}) => isActive ? "active-page" : ""}><Icon icon="solar:global-linear" className="menu-icon" /><span>Locations</span></NavLink></li>
            <li><NavLink to="/locations-google" className={({isActive}) => isActive ? "active-page" : ""}><Icon icon="solar:streets-map-point-linear" className="menu-icon" /><span>Google Places</span></NavLink></li>

            <li className="sidebar-menu-group-title">Main Locations</li>
            <li><NavLink to="/locations-main" className={({isActive}) => isActive ? "active-page" : ""}><Icon icon="solar:city-outline" className="menu-icon" /><span>List View</span></NavLink></li>
            <li><NavLink to="/google-business" className={({isActive}) => isActive ? "active-page" : ""}><Icon icon="solar:streets-map-point-linear" className="menu-icon" /><span>Google Businesses</span></NavLink></li>

            <li className="sidebar-menu-group-title">Sub Locations</li>
            <li><NavLink to="/locations-sub" className={({isActive}) => isActive ? "active-page" : ""}><Icon icon="solar:shop-outline" className="menu-icon" /><span>List View</span></NavLink></li>

            <li className="sidebar-menu-group-title">Client Information</li>
            <li><NavLink to="/company" className={({isActive}) => isActive ? "active-page" : ""}><Icon icon="solar:buildings-2-linear" className="menu-icon" /><span>Company</span></NavLink></li>
            <li><NavLink to="/users" className={({isActive}) => isActive ? "active-page" : ""}><Icon icon="solar:user-outline" className="menu-icon" /><span>User Management</span></NavLink></li>

            <li className="sidebar-menu-group-title">Support</li>
            <li><a href="https://support.qiu-one.com" target="_blank" rel="noreferrer"><Icon icon="solar:notes-outline" className="menu-icon" /><span>Ticket System</span></a></li>
          </ul>
        </div>
      </aside>

      {/* ========== MAIN CONTENT AREA ========== */}
      <main className={`dashboard-main ${sidebarActive ? "active" : ""}`}>
        <div className="navbar-header">
          <div className="row align-items-center justify-content-between">
            <div className="col-auto">
              <button type="button" className="sidebar-toggle" onClick={() => setSidebarActive(!sidebarActive)}>
                <Icon icon={sidebarActive ? "iconoir:arrow-right" : "heroicons:bars-3-solid"} className="icon text-2xl" />
              </button>
            </div>

            <div className="col-auto">
              <div className="d-flex align-items-center gap-3">
                <ThemeToggleButton />
                
                {/* PROFILE DROPDOWN: Fixed Toggle logic */}
                <div className="dropdown" ref={dropdownRef}>
                  <button 
                    className="border-0 bg-transparent p-0 d-flex align-items-center"
                    type="button"
                    onClick={() => setShowProfileMenu(!showProfileMenu)}
                  >
                    <div className="w-40-px h-40-px rounded-circle bg-primary text-white d-flex align-items-center justify-content-center fw-bold shadow-sm">
                      {getInitial()}
                    </div>
                  </button>

                  <ul 
                    className={`dropdown-menu to-top shadow-lg radius-12 border-0 py-0 overflow-hidden ${showProfileMenu ? 'show' : ''}`}
                    style={{ 
                        minWidth: '230px', 
                        display: showProfileMenu ? 'block' : 'none',
                        position: 'absolute',
                        right: 0,
                        top: '45px',
                        zIndex: 1000
                    }}
                  >
                    <div className="py-16 px-16 bg-primary-50 border-bottom border-white-light">
                      <h6 className="text-sm mb-0 text-primary-light fw-bold">{user?.name || "User Account"}</h6>
                      <span className="text-xs text-secondary-light">{user?.email || "Administrator"}</span>
                    </div>
                    
                    <div className="p-8">
                      <li>
                        <Link to="/profile" className="dropdown-item radius-8 text-black px-16 py-10 hover-bg-primary-50 hover-text-primary-600 d-flex align-items-center gap-3 border-0 bg-transparent transition-all" onClick={() => setShowProfileMenu(false)}>
                          <Icon icon="solar:user-circle-outline" className="icon text-xl text-primary-light" /> 
                          <span className="fw-semibold">My Profile</span>
                        </Link>
                      </li>
                      <li>
                        <button className="dropdown-item radius-8 text-black px-16 py-10 hover-bg-danger-focus hover-text-danger-main d-flex align-items-center gap-3 border-0 bg-transparent w-100 transition-all" onClick={handleLogout}>
                          <Icon icon="lucide:power" className="icon text-xl text-danger-main" /> 
                          <span className="fw-semibold">Sign Out</span>
                        </button>
                      </li>
                    </div>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="dashboard-main-body">
            {children}
        </div>
      </main>
    </section>
  );
};

export default MasterLayout;